/**
 * A tiny, dependency-free finite state machine.
 *
 * WHY this exists:
 * Order / table / production / task lifecycles are the part of a restaurant
 * system where bugs cost real money (a dish cooked twice, an order paid twice,
 * a table double-booked). Those lifecycles are enforced in three places — the
 * D1 `CHECK (status IN (...))` constraints, the API handlers and the three
 * PWAs — and they MUST agree. Encoding each lifecycle as one declarative
 * transition table, shared by every layer, is the only way to keep them in
 * sync: the API validates against it, the POS greys out buttons from it, and
 * the permission attached to each edge is what the guard middleware checks.
 *
 * WHY it is pure and synchronous:
 * The same code runs inside a Durable Object (realtime table/KDS state), in a
 * Worker request handler, and in the browser while offline. It therefore may
 * not touch the network, the clock or the database — a transition decision has
 * to be reproducible from `(from, event)` alone so that an offline client and
 * the server reach the same conclusion when the outbox is replayed.
 */

import { errors } from '@sonauto/utilities';

/**
 * One edge of a lifecycle graph.
 *
 * `permission` is stored on the edge rather than on the handler because the
 * same status change can be reached from several endpoints (POS, Staff App,
 * dispatcher) and we want exactly one answer to "who may do this?".
 *
 * `guardName` names a business rule that the state graph itself cannot express
 * (e.g. "only if no settled payment exists"). The machine never evaluates it —
 * it only advertises that the caller still has to. Keeping it declarative lets
 * the Admin app render an accurate lifecycle diagram.
 */
export interface Transition<S, E> {
  readonly from: S;
  readonly event: E;
  readonly to: S;
  readonly permission?: string;
  readonly guardName?: string;
}

/**
 * A whole lifecycle definition. Declared `readonly` so a machine can be built
 * once at module scope and safely shared across requests inside an isolate.
 */
export type TransitionTable<S, E> = readonly Transition<S, E>[];

/** Construction-time options; both are optional so the common case is one argument. */
export interface StateMachineOptions<S extends string> {
  /**
   * Human name used in error messages ("đơn hàng", "bàn", ...) so the Vietnamese
   * message a cashier sees says what actually failed.
   */
  readonly name?: string;
  /**
   * States that are final even though the table happens to give them outgoing
   * edges. Needed for cases like an order that is technically re-openable but
   * must be treated as closed by reporting.
   */
  readonly terminalStates?: readonly S[];
}

/**
 * Immutable lifecycle graph with O(1) lookup.
 *
 * WHY a class and not a bag of functions: the `(from, event)` index is built
 * once at module load and reused for every request in the isolate; rebuilding
 * it per call would be wasteful on the hot POS path (every order mutation
 * consults it).
 */
export class StateMachine<S extends string, E extends string> {
  /** The original table, exposed so the Admin app can render a lifecycle diagram. */
  readonly transitions: TransitionTable<S, E>;

  private readonly name: string;
  private readonly byFrom: Map<S, Map<E, Transition<S, E>>>;
  private readonly eventTargets: Map<E, Set<S>>;
  private readonly explicitTerminal: ReadonlySet<S> | null;

  constructor(transitions: TransitionTable<S, E>, options: StateMachineOptions<S> = {}) {
    this.transitions = transitions;
    this.name = options.name ?? 'bản ghi';
    this.byFrom = new Map();
    this.eventTargets = new Map();
    this.explicitTerminal = options.terminalStates ? new Set(options.terminalStates) : null;

    for (const t of transitions) {
      let row = this.byFrom.get(t.from);
      if (row === undefined) {
        row = new Map();
        this.byFrom.set(t.from, row);
      }
      if (row.has(t.event)) {
        // A duplicate edge means the lifecycle is ambiguous — fail loudly at
        // module load rather than silently picking one branch in production.
        throw errors.internal(
          `Bảng chuyển trạng thái bị trùng: ${String(t.from)} + ${String(t.event)} được khai báo nhiều lần.`,
        );
      }
      row.set(t.event, t);

      let targets = this.eventTargets.get(t.event);
      if (targets === undefined) {
        targets = new Set();
        this.eventTargets.set(t.event, targets);
      }
      targets.add(t.to);
    }
  }

  /** Is `event` legal in `from`? Used by the UIs to enable/disable buttons. */
  can(from: S, event: E): boolean {
    return this.byFrom.get(from)?.has(event) === true;
  }

  /**
   * Resulting state, or `null` when the edge does not exist.
   * Prefer this in read-only/projection code; use {@link assertTransition} when
   * the caller is actually mutating and an illegal move must become a 409.
   */
  next(from: S, event: E): S | null {
    return this.byFrom.get(from)?.get(event)?.to ?? null;
  }

  /**
   * Every event legal in `from`, in table declaration order.
   *
   * Declaration order is deliberate: the POS renders action buttons in this
   * order, so the table doubles as the UI ordering spec.
   */
  allowedEvents(from: S): E[] {
    const row = this.byFrom.get(from);
    return row === undefined ? [] : [...row.keys()];
  }

  /**
   * Permission code guarding an edge, or `undefined` when the edge is open to
   * anyone holding a valid session (e.g. a guest submitting their own QR order,
   * or a system/dispatcher-driven move). Never returns a default — an accidental
   * `undefined` must be visible, not silently substituted with something strict.
   */
  requiredPermission(from: S, event: E): string | undefined {
    return this.byFrom.get(from)?.get(event)?.permission;
  }

  /** Business rule the caller must still evaluate itself, if the edge declares one. */
  guardName(from: S, event: E): string | undefined {
    return this.byFrom.get(from)?.get(event)?.guardName;
  }

  /**
   * A state is terminal when nothing can leave it. Reporting and the sync
   * engine use this to decide what may be pruned from the realtime working set.
   */
  isTerminal(state: S): boolean {
    if (this.explicitTerminal !== null) return this.explicitTerminal.has(state);
    const row = this.byFrom.get(state);
    return row === undefined || row.size === 0;
  }

  /**
   * Like {@link next} but throws `INVALID_STATE` (HTTP 409) instead of
   * returning `null`.
   *
   * WHY it throws rather than returns a Result: an illegal transition is almost
   * always a stale client (two waiters tapping "Phục vụ" on the same dish), and
   * every call site would otherwise repeat the same rethrow. The Vietnamese
   * message names the states involved so the staff member sees why their tap
   * was rejected without needing a support ticket.
   */
  assertTransition(from: S, event: E): S {
    const to = this.next(from, event);
    if (to !== null) return to;

    const targets = this.eventTargets.get(event);
    // When an event always leads to the same state we can name that state too,
    // which is what makes the message actionable ("từ X sang Y").
    const intendedTo = targets !== undefined && targets.size === 1 ? [...targets][0] : undefined;
    const allowed = this.allowedEvents(from);

    const message =
      intendedTo !== undefined
        ? `Không thể chuyển ${this.name} từ trạng thái "${from}" sang "${intendedTo}" bằng thao tác "${event}".`
        : `Không thể thực hiện thao tác "${event}" khi ${this.name} đang ở trạng thái "${from}".`;

    throw errors.invalidState(message, {
      machine: this.name,
      from,
      event,
      intendedTo,
      allowedEvents: allowed,
    });
  }
}
